<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0">
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in activeRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : buildRouteTo(route)"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :border-color="acriveBorderColor"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon
                v-if="route.meta.icon"
                aria-hidden="true"
                :name="route.meta.icon"
                size="19px"
                :color="iconColor(route)"
                class="material-symbols-outlined"
              />
              <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                {{ t(route.displayName) }}
                <VaIcon
                  v-if="route.children"
                  class="material-symbols-outlined"
                  :name="arrowDirection(isCollapsed)"
                  size="19px"
                />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="buildRouteTo(childRoute)"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import { mainRoutes, teamRoutes, type INavigationRoute } from './NavigationRoutes'

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const { getColor } = useColors()
    const route = useRoute()
    const { t } = useI18n()

    const value = ref<boolean[]>([])

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isTeamMode = computed(() => {
      if (route.meta?.teamContext === 'inside') return true
      return !!route.params?.teamId
    })

    const activeRoutes = computed<INavigationRoute[]>(() => (isTeamMode.value ? teamRoutes : mainRoutes))

    const buildRouteTo = (target: INavigationRoute) => {
      const base: { name: string; params?: Record<string, string> } = { name: target.name }
      // Team-mode routes require the current teamId in params.
      if (target.name.startsWith('team-')) {
        const tid = route.params?.teamId
        if (tid) base.params = { teamId: String(Array.isArray(tid) ? tid[0] : tid) }
      }
      return base
    }

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.name === section.name
      }

      return section.children.some(({ name }) => route.name === name)
    }

    const setActiveExpand = () =>
      (value.value = activeRoutes.value.map((r: INavigationRoute) => routeHasActiveChild(r)))

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const acriveBorderColor = computed(() => getColor('primary'))

    const activeColor = computed(() => '#fff')

    const iconColor = (r: INavigationRoute) => (routeHasActiveChild(r) ? 'primary' : 'secondary')
    const textColor = (r: INavigationRoute) => (routeHasActiveChild(r) ? 'primary' : 'textPrimary')
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })
    watch(activeRoutes, setActiveExpand)

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      acriveBorderColor,
      activeRoutes,
      buildRouteTo,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
    }
  },
})
</script>
